import { useCallback, useMemo, useRef, useState } from "react";
import { offset } from "@floating-ui/react";
import DatePicker, { registerLocale } from "react-datepicker";
import fr from "date-fns/locale/fr";
import Button from "../button";
import { dateShortText2Local } from "../../utils/Date";
import { createDatePickerCustomHeader } from "./DatePickerCustomHeader";

registerLocale("fr", fr);

const stopPickerClose = (e) => {
  e.preventDefault();
  e.stopPropagation();
};

/**
 * Plage (react-datepicker) avec brouillon + Annuler / Appliquer par défaut.
 * Un jour unique = 1 clic sur une date puis Appliquer (ou 2e clic le même jour pour une plage d’un seul jour).
 * Si `applyOnSelect` : pas de pied de calendrier ; à la sélection complète de la plage (début + fin), appelle `onApply` et ferme.
 */
const RangeDatePicker = ({
  startDate = null,
  endDate = null,
  onApply,
  /** Sans boutons ; applique et ferme dès que début et fin sont connus (dont un seul jour : 2e clic le même jour si besoin). */
  applyOnSelect = false,
  customInput = undefined,
  className,
  maxDate,
  minDate,
  dateFormat = "dd/MM/yyyy",
  locale = "fr",
  peekNextMonth = true,
  monthsShown = 1,
  dropdownMode = "select",
  popperPlacement = "bottom",
  placeholderText,
  selectsRange = true,
}) => {
  const pickerRef = useRef(null);
  const [draftStart, setDraftStart] = useState(startDate);
  const [draftEnd, setDraftEnd] = useState(endDate);

  const syncDraftFromProps = useCallback(() => {
    setDraftStart(startDate ?? null);
    setDraftEnd(endDate ?? null);
  }, [startDate, endDate]);

  const onCalendarOpen = useCallback(() => {
    syncDraftFromProps();
  }, [syncDraftFromProps]);

  const handleChange = (dates) => {
    if (!selectsRange) {
      setDraftStart(dates);
      setDraftEnd(null);
      if (!applyOnSelect || !dates) return;
      onApply({ start: dates, end: null });
      pickerRef.current?.setOpen(false);
      return;
    }

    const [s, e] = dates || [null, null];
    setDraftStart(s);
    setDraftEnd(e);
    if (!applyOnSelect || !s || e == null) return;
    onApply({ start: s, end: e });
    pickerRef.current?.setOpen(false);
  };

  const handleApply = (e) => {
    stopPickerClose(e);
    if (!draftStart) return;
    const end = selectsRange ? draftEnd ?? draftStart : null;
    onApply({ start: draftStart, end });
    pickerRef.current?.setOpen(false);
  };

  const handleCancel = (e) => {
    stopPickerClose(e);
    syncDraftFromProps();
    pickerRef.current?.setOpen(false);
  };

  const hideYearStart =
    Boolean(draftStart) &&
    (!draftEnd || draftStart.getFullYear() === draftEnd.getFullYear());

  const hideYearEnd =
    Boolean(draftStart && draftEnd) &&
    draftStart.getFullYear() === draftEnd.getFullYear();

  const startLabel = useMemo(
    () =>
      draftStart
        ? dateShortText2Local(draftStart, {
            hideYear: hideYearStart,
          })
        : "—",
    [draftStart, hideYearStart],
  );
  const endLabel = useMemo(
    () =>
      draftEnd
        ? dateShortText2Local(draftEnd, {
            hideYear: hideYearEnd,
          })
        : "—",
    [draftEnd, hideYearEnd],
  );

  const rangeSummary = useMemo(
    () => (
      <div className="react-datepicker-ignore-onclickoutside box-border w-full max-w-full min-w-0 pb-2">
        <div className="flex w-full max-w-full min-w-0 box-border overflow-hidden rounded-lg border border-gray-100">
          <div className="min-w-0 flex-1 px-3 py-2.5 text-left">
            <div className="ft-xs text-gray-500">Date de début</div>
            <div className="ft-sm font-medium text-gray-900 mt-0.5">
              {startLabel}
            </div>
          </div>
          {selectsRange ? (
            <>
              <div
                className="w-px shrink-0 self-stretch bg-gray-100"
                aria-hidden="true"
              />
              <div className="min-w-0 flex-1 px-3 py-2.5 text-left">
                <div className="ft-xs text-gray-500">Date de fin</div>
                <div className="ft-sm font-medium text-gray-900 mt-0.5">
                  {endLabel}
                </div>
              </div>
            </>
          ) : null}
        </div>
      </div>
    ),
    [startLabel, endLabel, selectsRange],
  );

  const renderCustomHeader = useMemo(
    () =>
      createDatePickerCustomHeader({
        minDate,
        maxDate,
        useShortMonth: false,
        monthsShown,
        betweenNavAndDayNames: rangeSummary,
      }),
    [minDate, maxDate, monthsShown, rangeSummary],
  );

  return (
    <DatePicker
      ref={pickerRef}
      selectsRange={selectsRange}
      shouldCloseOnSelect={false}
      selected={selectsRange ? undefined : draftStart}
      startDate={selectsRange ? draftStart : undefined}
      endDate={selectsRange ? draftEnd : undefined}
      onChange={handleChange}
      onCalendarOpen={onCalendarOpen}
      dateFormat={dateFormat}
      locale={locale}
      peekNextMonth={peekNextMonth}
      showMonthDropdown={false}
      showYearDropdown={false}
      monthsShown={monthsShown}
      renderCustomHeader={renderCustomHeader}
      dropdownMode={dropdownMode}
      popperPlacement={popperPlacement}
      popperModifiers={[offset({ mainAxis: -2 })]}
      className={className}
      customInput={customInput}
      maxDate={maxDate}
      minDate={minDate}
      placeholderText={placeholderText}
    >
      {!applyOnSelect ? (
        <div className="react-datepicker-ignore-onclickoutside flex w-full flex-col gap-2 px-3 py-2">
          <div className="flex justify-end gap-2">
            <Button color="SECONDARY" size="SM" onClick={handleCancel}>
              Annuler
            </Button>
            <Button
              color="PRIMARY"
              size="SM"
              disabled={!draftStart}
              onClick={handleApply}
            >
              Appliquer
            </Button>
          </div>
        </div>
      ) : null}
    </DatePicker>
  );
};

export default RangeDatePicker;
